import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, fmtBtc, fmtFiat, fmtDate, short } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard, StatusBadge, PageHeader, Empty } from "@/components/shared";

export default function Overview() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [wallets, setWallets] = useState([]);
  useEffect(() => {
    api.get("/merchant/stats").then((r) => setS(r.data));
    api.get("/merchant/wallets").then((r) => setWallets(r.data));
  }, [user?.merchant?.mode]);

  const mode = user?.merchant?.mode;
  const network = mode === "live" ? "mainnet" : "testnet";
  const hasWallet = wallets.some((w) => w.active && w.network === network);
  const gap = wallets.find((w) => w.active && w.gap_limit_warning);

  return (
    <div>
      <PageHeader title="Vue d'ensemble" subtitle={`Mode ${mode === "live" ? "LIVE (mainnet)" : "TEST (testnet)"} — les statistiques affichées concernent ce mode uniquement.`} />
      {!hasWallet && (
        <div className="panel p-4 mb-6 border-amber-500/40 bg-amber-500/5 text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="setup-wallet-alert">
          <span>Aucun wallet <b>{network}</b> actif : ajoutez une {network === "mainnet" ? "zpub" : "vpub/tpub"} pour recevoir des paiements en mode {mode}.</span>
          <Link to="/app/wallet" className="px-3 py-1.5 rounded-md bg-amber-500 text-zinc-950 font-semibold text-xs w-fit">Configurer</Link>
        </div>
      )}
      {gap && <div className="panel p-4 mb-6 border-red-500/40 bg-red-500/5 text-sm" data-testid="gap-limit-alert">Alerte gap limit : {gap.unused_addresses} adresses dérivées sans paiement sur le wallet {gap.network}. Certains wallets (gap 20) peuvent ne plus afficher les nouveaux paiements.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard label="Volume encaissé" value={s ? fmtBtc(s.volume_btc) : "—"} hint={s ? `≈ ${fmtFiat(s.volume_fiat)}` : ""} testId="stat-total-volume" accent="text-amber-400" />
        <StatCard label="Commandes" value={s?.total_orders ?? "—"} hint={s ? `${s.paid_orders} payées` : ""} testId="stat-total-orders" />
        <StatCard label="Sites actifs" value={s?.active_sites ?? "—"} testId="stat-active-sites" />
        <StatCard label="Taux de succès" value={s ? `${s.success_rate}%` : "—"} hint={s ? `${s.pending} en attente` : ""} testId="stat-success-rate" accent="text-emerald-400" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="panel p-5 lg:col-span-2" data-testid="volume-chart-container">
          <div className="eyebrow mb-4">Volume journalier (fiat)</div>
          {s?.daily_volume?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={s.daily_volume}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F7931A" stopOpacity={0.5} /><stop offset="100%" stopColor="#F7931A" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="#27272A" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#71717A", fontSize: 11 }} width={50} />
                <Tooltip contentStyle={{ background: "#121215", border: "1px solid #27272A", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="amount" stroke="#F7931A" fill="url(#g)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Empty text="Aucun paiement confirmé pour l'instant." />}
        </div>
        <div className="panel p-5">
          <div className="eyebrow mb-4">Dernières commandes</div>
          <div className="space-y-3">
            {s?.recent?.length ? s.recent.map((o) => (
              <Link to="/app/orders" key={o.id} className="flex items-center justify-between text-sm hover:bg-zinc-800/50 -mx-2 px-2 py-1.5 rounded transition-colors" data-testid="recent-order-row">
                <div><div className="font-mono text-xs text-zinc-300">{short(o.id, 12, 4)}</div><div className="text-[11px] text-zinc-500">{fmtDate(o.created_at)}</div></div>
                <div className="text-right"><div className="text-xs">{fmtFiat(o.amount_fiat, o.currency)}</div><StatusBadge status={o.status} /></div>
              </Link>
            )) : <Empty text="Aucune commande." />}
          </div>
        </div>
      </div>
    </div>
  );
}
